import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { Icon } from "@iconify/react";

// ** Components
import AppModal from "@/components/modal/AppModal.tsx";
import {
  EmailTemplateForm,
  type EmailTemplateFormSubmitData,
} from "@/views/admin/emails/EmailTemplateForm.tsx";

// ** Hooks
import {
  useGetEmailTemplatesQuery,
  useUpdateEmailTemplateMutation,
} from "@/api/emailTemplatesApi.ts";

// ** Utils
import { mutationFeedback } from "@/services/utils.tsx";

// ** Types
import type { components } from "@/types/api/generated-api-schema.ts";

type EmailTemplate =
  components["schemas"]["EmailTemplate.jsonld-email_template.read_read"];

export const Route = createFileRoute("/admin/emails")({
  component: EmailTemplatesPage,
});

function EmailTemplatesPage() {
  const { t } = useTranslation();

  // Modal states
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);

  // API
  const { data, isLoading, error } = useGetEmailTemplatesQuery({});
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedTemplate(null);
  };

  const handleTemplateSubmit = async (data: EmailTemplateFormSubmitData) => {
    if (!selectedTemplate?.uuid) return;

    const result = await updateTemplate({
      uuid: selectedTemplate.uuid,
      data: {
        subjectFr: data.subjectFr,
        subjectEn: data.subjectEn,
        bodyFr: data.bodyFr,
        bodyEn: data.bodyEn,
        attachments: data.attachments,
      },
    });

    mutationFeedback(result, {
      success: t("admin.emails.messages.updated"),
      error: t("admin.emails.messages.updateError"),
    });

    if (!("error" in result)) {
      handleFormClose();
    }
  };

  const templates = data?.member ?? [];

  if (error) {
    return (
      <Box>
        <Typography color="error">{t("admin.common.loadingError")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight={600} color="text.primary">
          {t("admin.emails.title")}
        </Typography>
      </Stack>

      <Typography color="text.secondary" mb={3}>
        {t("admin.emails.subtitle")}
      </Typography>

      {/* Content */}
      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={100} />
          ))}
        </Stack>
      ) : templates.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t("admin.emails.noTemplates")}
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {templates.map((template) => (
            <EmailTemplateCard
              key={template.uuid}
              template={template}
              onEdit={() => handleEdit(template)}
              t={t}
            />
          ))}
        </Stack>
      )}

      {/* Form Modal - key forces remount when switching templates */}
      {selectedTemplate && (
        <AppModal
          key={selectedTemplate.uuid}
          open={formOpen}
          onClose={handleFormClose}
          title={selectedTemplate.typeEnum?.label ?? t("admin.emails.editTemplate")}
          sx={{ width: 700, maxHeight: "90vh", overflow: "auto" }}
        >
          <EmailTemplateForm
            template={selectedTemplate}
            onSubmit={handleTemplateSubmit}
            onCancel={handleFormClose}
            isSubmitting={isUpdating}
          />
        </AppModal>
      )}
    </Box>
  );
}

// EmailTemplateCard component
type CardProps = {
  template: EmailTemplate;
  onEdit: () => void;
  t: (key: string) => string;
};

function EmailTemplateCard({ template, onEdit, t }: CardProps) {
  return (
    <Card>
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1,
            bgcolor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon icon="tabler:mail" width={24} color="white" />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="h6" fontWeight={600}>
              {template.typeEnum?.label ?? template.type}
            </Typography>
            <Chip label={template.type} size="small" variant="outlined" />
          </Stack>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {template.typeEnum?.description}
          </Typography>
          <Typography variant="body2" noWrap>
            <strong>FR:</strong> {template.subjectFr}
          </Typography>
          <Typography variant="body2" noWrap>
            <strong>EN:</strong> {template.subjectEn}
          </Typography>
        </Box>

        {/* Actions */}
        <Tooltip title={t("admin.common.edit")}>
          <IconButton onClick={onEdit}>
            <Icon icon="tabler:edit" width={20} />
          </IconButton>
        </Tooltip>
      </CardContent>
    </Card>
  );
}
